import React, { useEffect, useMemo, useState } from "react";
import API from "../../api";
import "./notes.css";
import { useDispatch, useSelector } from "react-redux";
import { selectUser, setUser } from "../../store/authSlice";

const PRIORITIES = [
    { key: "all", label: "All" },
    { key: "low", label: "Low" },
    { key: "medium", label: "Medium" },
    { key: "high", label: "High" },
];

function fmtDateTime(v) {
    if (!v) return "-";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleString();
}

function priorityClass(p) {
    if (p === "high") return "pr-high";
    if (p === "low") return "pr-low";
    return "pr-medium";
}

function safeJsonParse(s) {
    try {
        return JSON.parse(s);
    } catch {
        return null;
    }
}

/** JWT decode (if you store a JWT token). */
function getUserFromToken() {
    const tok = window.localStorage.getItem("token") || window.sessionStorage.getItem("token");
    if (!tok) return {};
    const parts = tok.split(".");
    if (parts.length < 2) return {};
    try {
        const json = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
        return json?.user || json || {};
    } catch {
        return {};
    }
}

/**
 * Read persisted auth from redux-persist.
 * Key is usually `persist:root` (older builds sometimes `persistroot`).
 */
function getPersistedAuth() {
    const keysToTry = ["persist:root", "persistroot"];
    for (const key of keysToTry) {
        const raw = window.localStorage.getItem(key);
        if (!raw) continue;

        const root = safeJsonParse(raw);
        if (!root) continue;

        // many setups store auth slice as a JSON-string inside the root
        const authObj = typeof root?.auth === "string" ? safeJsonParse(root.auth) : root?.auth;
        if (authObj) return authObj;
    }
    return null;
}

/**
 * IMPORTANT:
 * Prefer redux-persist auth,
 * then token,
 * then localStorage user object,
 * then last resort localStorage role.
 */
function getCurrentAuth() {
    // 1) persisted
    const persisted = getPersistedAuth();
    if (persisted) {
        const pUser = persisted?.user || persisted;
        const id = pUser?.id ?? persisted?.id ?? pUser?._id ?? persisted?._id ?? "";
        const username = pUser?.username || persisted?.username || "";
        const role = pUser?.role || persisted?.role || "";
        return {
            id: id === "" ? "" : Number(id) || String(id),
            username: String(username || ""),
            role: String(role || ""),
        };
    }

    // 2) token
    const tUser = getUserFromToken();
    const tId = tUser?.id ?? tUser?._id ?? "";
    const tUsername = tUser?.username || tUser?.preferred_username || "";
    const tRole = tUser?.role || "";
    if (tId || tUsername || tRole) {
        return {
            id: tId === "" ? "" : Number(tId) || String(tId),
            username: String(tUsername || ""),
            role: String(tRole || ""),
        };
    }

    // 3) localStorage user object
    const rawUser = window.localStorage.getItem("user");
    if (rawUser) {
        const uObj = safeJsonParse(rawUser);
        if (uObj) {
            const uId = uObj?.id ?? uObj?._id ?? "";
            const uUsername = uObj?.username || "";
            const uRole = uObj?.role || "";
            if (uId || uUsername || uRole) {
                return {
                    id: uId === "" ? "" : Number(uId) || String(uId),
                    username: String(uUsername || ""),
                    role: String(uRole || ""),
                };
            }
        }
    }

    // 4) last resort localStorage role
    const lsRole = window.localStorage.getItem("role") || "";
    return { id: "", username: "", role: String(lsRole) };
}

export default function Notes() {
    const dispatch = useDispatch();
    const user = useSelector(selectUser);

    // local auth snapshot (id/username/role) using same technique as drafts.jsx
    const [authSnap, setAuthSnap] = useState(() => getCurrentAuth());

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    // modal
    const [openAdd, setOpenAdd] = useState(false);

    // add fields
    const [newTitle, setNewTitle] = useState("");
    const [newNote, setNewNote] = useState("");
    const [newPriority, setNewPriority] = useState("medium");

    // edit state
    const [editingId, setEditingId] = useState(null);
    const [editTitle, setEditTitle] = useState("");
    const [editNote, setEditNote] = useState("");
    const [editPriority, setEditPriority] = useState("medium");

    // selection bulk delete
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    // compact filters (one line)
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [createdFrom, setCreatedFrom] = useState("");
    const [createdTo, setCreatedTo] = useState("");
    const [updatedFrom, setUpdatedFrom] = useState("");
    const [updatedTo, setUpdatedTo] = useState("");

    // Ensure user/auth info is present.
    // Priority:
    // 1) local redux-persist/token/localStorage (authSnap)
    // 2) /auth/me (cookie session)
    useEffect(() => {
        let ignore = false;
        (async () => {
            try {
                const snap = getCurrentAuth();
                if (!ignore) setAuthSnap(snap);

                // hydrate redux store for UI (optional)
                if (!user && (snap?.username || snap?.role || snap?.id)) {
                    if (!ignore) dispatch(setUser({ ...snap }));
                }

                // cookie session user fallback
                if (!user && (!snap?.username || !snap?.role || !snap?.id)) {
                    const { data } = await API.get("/auth/me");
                    if (!ignore && data?.user) {
                        dispatch(setUser(data.user));
                        // also update local snapshot if backend returns id
                        setAuthSnap((prev) => ({
                            id: data.user?.id ?? prev?.id ?? "",
                            username: data.user?.username ?? prev?.username ?? "",
                            role: data.user?.role ?? prev?.role ?? "",
                        }));
                    }
                }
            } catch {
                // ignore
            }
        })();
        return () => {
            ignore = true;
        };
    }, [user, dispatch]);

    const loadNotes = async () => {
        setLoading(true);
        setErr("");
        try {
            const userId = authSnap?.id || user?.id;
            const params = {
                userId: userId || undefined,
                priority: priorityFilter,
                search: search || undefined,
                createdFrom: createdFrom || undefined,
                createdTo: createdTo || undefined,
                updatedFrom: updatedFrom || undefined,
                updatedTo: updatedTo || undefined,
            };
            const { data } = await API.get("/auth/user-notes", { params });
            setItems(Array.isArray(data?.items) ? data.items : []);
            setSelectedIds(new Set());
        } catch (e) {
            console.error(e);

            const status = e?.response?.status;
            const backendMsg = e?.response?.data?.error || e?.response?.data?.message;

            let friendly = "Could not load notes.";

            if (status === 401) {
                friendly =
                    "Not authenticated (401). Please login again. If you're already logged in, your cookie/token is not reaching the backend (check withCredentials + CORS credentials).";
            } else if (status === 403) {
                friendly = "Forbidden (403). You don't have permission to access notes.";
            } else if (backendMsg) {
                friendly = `Error${status ? ` (${status})` : ""}: ${backendMsg}`;
            } else if (status) {
                friendly = `Error (${status}). Could not load notes.`;
            }

            setErr(friendly);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadNotes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authSnap?.id]);

    // debounce filters
    useEffect(() => {
        const t = setTimeout(() => loadNotes(), 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priorityFilter, search, createdFrom, createdTo, updatedFrom, updatedTo]);

    const allSelected = useMemo(() => {
        if (!items.length) return false;
        return items.every((x) => selectedIds.has(x.id));
    }, [items, selectedIds]);

    const toggleAll = () => {
        setSelectedIds((prev) => {
            if (!items.length) return prev;
            const every = items.every((x) => prev.has(x.id));
            if (every) return new Set();
            const next = new Set(prev);
            items.forEach((x) => next.add(x.id));
            return next;
        });
    };

    const toggleOne = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const openAddModal = () => {
        setErr("");
        setNewTitle("");
        setNewNote("");
        setNewPriority("medium");
        setOpenAdd(true);
    };

    const addNote = async (e) => {
        e.preventDefault();
        setErr("");

        const title = newTitle.trim();
        const note = newNote.trim();
        if (!title || !note) {
            setErr("Title and Note are required.");
            return;
        }

        try {
            const userId = authSnap?.id || user?.id;
            await API.post("/auth/user-notes", {
                userId,
                username: user?.username || authSnap?.username,
                role: user?.role || authSnap?.role,
                title,
                note,
                priority: newPriority,
            });
            setOpenAdd(false);
            await loadNotes();
        } catch (e2) {
            console.error(e2);
            setErr("Could not add note.");
        }
    };

    const startEdit = (n) => {
        setEditingId(n.id);
        setEditTitle(n.title || "");
        setEditNote(n.note || "");
        setEditPriority(n.priority || "medium");
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditTitle("");
        setEditNote("");
        setEditPriority("medium");
    };

    const saveEdit = async (id) => {
        setErr("");
        const title = editTitle.trim();
        const note = editNote.trim();
        if (!title || !note) {
            setErr("Title and Note are required.");
            return;
        }
        try {
            const userId = authSnap?.id || user?.id;
            await API.put(`/auth/user-notes/${id}`, { userId, title, note, priority: editPriority });
            cancelEdit();
            await loadNotes();
        } catch (e) {
            console.error(e);
            setErr("Could not update note.");
        }
    };

    const deleteOne = async (id) => {
        setErr("");
        if (!window.confirm("Delete this note?")) return;
        try {
            const userId = authSnap?.id || user?.id;
            await API.delete(`/auth/user-notes/${id}`, { data: { userId } });
            await loadNotes();
        } catch (e) {
            console.error(e);
            setErr("Could not delete note.");
        }
    };

    const bulkDelete = async () => {
        setErr("");
        const ids = Array.from(selectedIds);
        if (!ids.length) return;
        if (!window.confirm(`Delete ${ids.length} selected notes?`)) return;
        try {
            const userId = authSnap?.id || user?.id;
            await API.post("/auth/user-notes/bulk-delete", { userId, ids });
            await loadNotes();
        } catch (e) {
            console.error(e);
            setErr("Could not bulk delete notes.");
        }
    };

    const clearFilters = () => {
        setPriorityFilter("all");
        setSearch("");
        setCreatedFrom("");
        setCreatedTo("");
        setUpdatedFrom("");
        setUpdatedTo("");
    };

    return (
        <div className="notes-wrap">
            <div className="notes-topbar">
                <div>
                    <h2 className="notes-title">My Notes</h2>
                    <div className="notes-sub">
                        Logged in as <b>{user?.username || authSnap?.username || "..."}</b> (
                        {user?.role || authSnap?.role || "..."})
                    </div>
                </div>

                <div className="notes-top-actions">
                    <button className="btn btn-danger" onClick={bulkDelete} disabled={!selectedIds.size}>
                        Bulk Delete ({selectedIds.size})
                    </button>

                    <button className="btn btn-primary btn-plus" onClick={openAddModal} title="Add Note">
                        + Add Note
                    </button>
                </div>
            </div>

            {err ? <div className="notes-error">{err}</div> : null}
            {loading ? <div className="notes-loading">Loading...</div> : null}

            <div className="notes-card">
                <table className="notes-table">
                    <thead>
                        <tr className="thead-title">
                            <th className="th-check">
                                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                            </th>
                            <th>Title / Note</th>
                            <th className="th-small">Priority</th>
                            <th className="th-time">Created</th>
                            <th className="th-time">Updated</th>
                            <th className="th-actions">Actions</th>
                        </tr>

                        {/* Filters in ONE LINE inside table head */}
                        <tr className="thead-filters">
                            <th></th>
                            <th>
                                <input
                                    className="mini-input"
                                    placeholder="Search title or note..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </th>
                            <th>
                                <select
                                    className="mini-select"
                                    value={priorityFilter}
                                    onChange={(e) => setPriorityFilter(e.target.value)}
                                >
                                    {PRIORITIES.map((p) => (
                                        <option key={p.key} value={p.key}>
                                            {p.label}
                                        </option>
                                    ))}
                                </select>
                            </th>
                            <th>
                                <div className="mini-dates">
                                    <input
                                        type="date"
                                        className="mini-date"
                                        value={createdFrom}
                                        onChange={(e) => setCreatedFrom(e.target.value)}
                                    />
                                    <span className="mini-sep">→</span>
                                    <input
                                        type="date"
                                        className="mini-date"
                                        value={createdTo}
                                        onChange={(e) => setCreatedTo(e.target.value)}
                                    />
                                </div>
                            </th>
                            <th>
                                <div className="mini-dates">
                                    <input
                                        type="date"
                                        className="mini-date"
                                        value={updatedFrom}
                                        onChange={(e) => setUpdatedFrom(e.target.value)}
                                    />
                                    <span className="mini-sep">→</span>
                                    <input
                                        type="date"
                                        className="mini-date"
                                        value={updatedTo}
                                        onChange={(e) => setUpdatedTo(e.target.value)}
                                    />
                                </div>
                            </th>
                            <th>
                                <button className="btn btn-light btn-mini" onClick={clearFilters} type="button">
                                    Clear
                                </button>
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {!loading && items.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="notes-empty">
                                    No notes found.
                                </td>
                            </tr>
                        ) : null}

                        {items.map((n) => {
                            const isEdit = editingId === n.id;

                            return (
                                <tr key={n.id} className="trow">
                                    <td className="td-check">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(n.id)}
                                            onChange={() => toggleOne(n.id)}
                                        />
                                    </td>

                                    <td className="td-main">
                                        {isEdit ? (
                                            <div className="edit-box">
                                                <input
                                                    className="edit-input"
                                                    value={editTitle}
                                                    onChange={(e) => setEditTitle(e.target.value)}
                                                />
                                                <textarea
                                                    className="edit-textarea"
                                                    value={editNote}
                                                    onChange={(e) => setEditNote(e.target.value)}
                                                    rows={3}
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <div className="note-title">{n.title}</div>
                                                <div className="note-body">{n.note}</div>
                                            </>
                                        )}
                                    </td>

                                    <td>
                                        {isEdit ? (
                                            <select
                                                className="mini-select"
                                                value={editPriority}
                                                onChange={(e) => setEditPriority(e.target.value)}
                                            >
                                                <option value="low">Low</option>
                                                <option value="medium">Medium</option>
                                                <option value="high">High</option>
                                            </select>
                                        ) : (
                                            <span className={`badge ${priorityClass(n.priority)}`}>
                                                {(n.priority || "medium").toUpperCase()}
                                            </span>
                                        )}
                                    </td>

                                    <td className="td-time">{fmtDateTime(n.created_at)}</td>
                                    <td className="td-time">{fmtDateTime(n.updated_at)}</td>

                                    <td className="td-actions">
                                        {isEdit ? (
                                            <>
                                                <button className="btn btn-primary btn-mini" onClick={() => saveEdit(n.id)}>
                                                    Save
                                                </button>
                                                <button className="btn btn-light btn-mini" onClick={cancelEdit}>
                                                    Cancel
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button className="btn btn-light btn-mini" onClick={() => startEdit(n)}>
                                                    Edit
                                                </button>
                                                <button className="btn btn-danger btn-mini" onClick={() => deleteOne(n.id)}>
                                                    Delete
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Add Note Modal */}
            {openAdd ? (
                <div className="modal-backdrop" onMouseDown={() => setOpenAdd(false)}>
                    <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="modal-head">
                            <div className="modal-title">Add Note</div>
                            <button className="btn btn-light btn-mini" onClick={() => setOpenAdd(false)}>
                                ✕
                            </button>
                        </div>

                        <form onSubmit={addNote} className="modal-body">
                            <input
                                className="edit-input"
                                placeholder="Title"
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                            />

                            <select
                                className="edit-input"
                                value={newPriority}
                                onChange={(e) => setNewPriority(e.target.value)}
                            >
                                <option value="low">Priority: Low</option>
                                <option value="medium">Priority: Medium</option>
                                <option value="high">Priority: High</option>
                            </select>

                            <textarea
                                className="edit-textarea"
                                placeholder="Write your note..."
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                rows={5}
                            />

                            <div className="modal-actions">
                                <button className="btn btn-primary" type="submit">
                                    Save Note
                                </button>
                                <button className="btn btn-light" type="button" onClick={() => setOpenAdd(false)}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
